import { useRef, useState } from 'react'
import type { Editor } from '@tiptap/core'
import {
  DEFAULT_LINE_HEIGHT,
  FONTS,
  FONT_KEYS,
  fontStack,
  formatNumber,
  MAX_FONT_SIZE,
  MAX_LINE_HEIGHT,
  MIN_FONT_SIZE,
  MIN_LINE_HEIGHT,
  parseNumber,
  type BoardTheme,
  type FontDto,
  type FontKey,
  type TextElement,
} from '@praesto/shared'
import { ColorPicker } from '../components/ColorPicker.js'
import { useKeepInView } from '../components/keepInView.js'
import { usePopover } from '../components/usePopover.js'
import { useLanguage, useText } from '../i18n.js'
import type { CameraStore } from './CameraStore.js'

/**
 * Die Leiste am ausgewählten Textfeld.
 *
 * **Zwei Bedeutungen derselben Knöpfe**, bewusst so: Wer gerade tippt, meint mit
 * „fett" die markierte Stelle. Wer den Text nur ausgewählt hat, meint das ganze
 * Feld — dort ändert derselbe Knopf die Grundeinstellung des Elements.
 *
 * Was dagegen *immer* am ganzen Feld hängt: Schrift, Schriftgröße und
 * Zeilenabstand. Sie bestimmen, wie das Feld auf der Folie wirkt, und ein Feld,
 * in dem drei Absätze in drei Schriften stehen, ist kein Entwurf, sondern ein
 * Versehen. Abstufungen innerhalb des Textes gibt es trotzdem: über die
 * Überschriften-Ebenen, die sich an der Grundgröße bemessen.
 *
 * Die Leiste rechnet den Board-Zoom heraus und bleibt darum auf dem Bildschirm
 * immer gleich groß — siehe `.text-settings` im Stylesheet.
 */

/** Zeilenabstände, die auf einer Folie tatsächlich vorkommen. */
const LINE_HEIGHTS = [1, 1.15, 1.25, 1.5, 2]

/** Was der Schriftwähler meint, wenn nicht eine bestimmte Schrift gemeint ist. */
const BOARD_HEADING = 'board:heading'
const BOARD_BODY = 'board:body'

interface Props {
  element: TextElement
  theme: BoardTheme
  /** Selbst gehostete Schriften dieser Organisation; leer bei Gästen. */
  fonts: readonly FontDto[]
  camera: CameraStore
  /** Läuft der Editor gerade? Dann wirken die Marken auf die Markierung. */
  editor: Editor | null
  onChange: (patch: Partial<TextElement>) => void
  /** Gehört der Leiste, damit der Aufrufer Klicks darin erkennen kann. */
  barRef: React.RefObject<HTMLDivElement | null>
  /** Wird gerade getippt? Dann darf der Klick auf die Leiste den Fokus nicht nehmen. */
  editing: boolean
  /** Nimmt das Feld beim Ziehen an einer Ecke die Schriftgröße mit? */
  scaleFont: boolean
  onToggleScaleFont?: () => void
}

/**
 * Ein frei eingegebenes Maß mit Schrittknöpfen.
 *
 * Der Entwurf steht während des Tippens getrennt vom Wert: Ohne ihn machte
 * jedes Zwischenergebnis eine gültige Zahl — wer „120" tippen will, hätte nach
 * dem ersten Zeichen Schriftgröße 1 und nach dem zweiten 12, und das Feld
 * springt ihm unter den Fingern weg.
 */
function MeasureField({
  value,
  min,
  max,
  step,
  digits = 0,
  title,
  onCommit,
}: {
  value: number
  min: number
  max: number
  step: number
  digits?: number
  title: string
  onCommit: (value: number) => void
}): React.ReactElement {
  const { language } = useLanguage()
  const t = useText()
  const [entwurf, setEntwurf] = useState<string | null>(null)
  /*
   * Escape verwirft — und muss das dem Verlassen des Feldes mitteilen können.
   *
   * Über eine Referenz und nicht über den Zustand: Das Verlassen folgt im selben
   * Zug und liest den Wert, der noch im Feld steht. Ohne diese Notiz übernähme
   * es genau den Entwurf, den Escape gerade wegwerfen sollte.
   */
  const verwerfen = useRef(false)

  const begrenzen = (wert: number): number =>
    Math.min(max, Math.max(min, Math.round(wert * 100) / 100))

  const uebernehmen = (text: string): void => {
    setEntwurf(null)
    const wert = parseNumber(text)
    if (wert !== null) onCommit(begrenzen(wert))
  }

  const schieben = (richtung: number): void => {
    setEntwurf(null)
    onCommit(begrenzen(value + richtung * step))
  }

  return (
    <span className="text-measure" title={title}>
      <button
        type="button"
        className="text-measure-step"
        onClick={() => schieben(-1)}
        tabIndex={-1}
        title={t('text.sizeSmaller')}
      >
        −
      </button>
      <input
        className="text-measure-input"
        // Krumme Werte entstehen beim Mitskalieren; gezeigt wird gerundet, damit
        // in einem Feld dieser Breite eine Zahl steht und keine Ziffernkette.
        value={entwurf ?? formatNumber(value, language, digits)}
        inputMode="decimal"
        aria-label={title}
        onChange={(event) => setEntwurf(event.target.value)}
        onBlur={(event) => {
          if (verwerfen.current) {
            verwerfen.current = false
            setEntwurf(null)
            return
          }
          uebernehmen(event.target.value)
        }}
        onKeyDown={(event) => {
          // Sonst löschte die Rücktaste im Eingabefeld das ausgewählte Element.
          event.stopPropagation()
          if (event.key === 'Enter') {
            uebernehmen(event.currentTarget.value)
            event.currentTarget.blur()
          } else if (event.key === 'Escape') {
            verwerfen.current = true
            event.currentTarget.blur()
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault()
            schieben(event.key === 'ArrowUp' ? 1 : -1)
          }
        }}
      />
      <button
        type="button"
        className="text-measure-step"
        onClick={() => schieben(1)}
        tabIndex={-1}
        title={t('text.sizeLarger')}
      >
        +
      </button>
    </span>
  )
}

/**
 * Der Schriftwähler.
 *
 * Zuoberst die beiden Schriften des Boards — sie sind der Regelfall, und wer
 * eine davon wählt, bleibt an das Board-Design gekoppelt. Darunter alles, was
 * sonst zur Verfügung steht: die eingebauten Stapel und die Schriften, die
 * diese Organisation selbst hinterlegt hat.
 */
function FontChoice({
  element,
  theme,
  fonts,
  camera,
  onChange,
}: {
  element: TextElement
  theme: BoardTheme
  fonts: readonly FontDto[]
  camera: CameraStore
  onChange: (patch: Partial<TextElement>) => void
}): React.ReactElement {
  const t = useText()
  const { open, toggle, close, ref } = usePopover<HTMLDivElement>()
  const menuRef = useKeepInView<HTMLDivElement>(camera)

  const gewaehlt: string = element.font || (element.serif ? BOARD_HEADING : BOARD_BODY)
  const nameVon = (key: FontKey): string =>
    key in FONTS ? t(FONTS[key as keyof typeof FONTS].label) : key
  const anzeige =
    gewaehlt === BOARD_HEADING
      ? t('text.fontHeading')
      : gewaehlt === BOARD_BODY
        ? t('text.fontBody')
        : nameVon(gewaehlt)

  const waehlen = (wert: string): void => {
    close()
    // Eine leere Schrift heißt „die des Boards"; welche das ist, entscheidet
    // weiterhin `serif`. So folgt das Feld einem später geänderten Board-Design.
    if (wert === BOARD_HEADING) onChange({ font: '', serif: true })
    else if (wert === BOARD_BODY) onChange({ font: '', serif: false })
    else onChange({ font: wert })
  }

  const eintrag = (wert: string, beschriftung: string, stapel: string): React.ReactElement => (
    <button
      key={wert}
      type="button"
      className={`text-menu-item${gewaehlt === wert ? ' is-on' : ''}`}
      style={{ fontFamily: stapel }}
      onClick={() => waehlen(wert)}
    >
      {beschriftung}
    </button>
  )

  return (
    <div className="text-font" ref={ref}>
      <button
        type="button"
        className="text-font-trigger"
        onClick={toggle}
        aria-expanded={open}
        title={t('text.font')}
      >
        <span className="text-font-name">{anzeige}</span>
        <span aria-hidden="true">⌄</span>
      </button>

      {open && (
        <div className="text-menu" ref={menuRef}>
          <span className="text-menu-label">{t('text.fontBoard')}</span>
          {eintrag(
            BOARD_HEADING,
            `${t('text.fontHeading')} · ${nameVon(theme.headingFont)}`,
            fontStack(theme.headingFont),
          )}
          {eintrag(
            BOARD_BODY,
            `${t('text.fontBody')} · ${nameVon(theme.bodyFont)}`,
            fontStack(theme.bodyFont),
          )}

          <span className="text-menu-label">{t('fonts.builtIn')}</span>
          {FONT_KEYS.map((key) => eintrag(key, t(FONTS[key].label), fontStack(key)))}

          {fonts.length > 0 && (
            <>
              <span className="text-menu-label">{t('fonts.added')}</span>
              {fonts.map((font) => eintrag(font.family, font.family, fontStack(font.family)))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** Zeilenabstand: Vorgaben zum Klicken, daneben die freie Eingabe. */
function LineSpacing({
  element,
  camera,
  onChange,
}: {
  element: TextElement
  camera: CameraStore
  onChange: (patch: Partial<TextElement>) => void
}): React.ReactElement {
  const t = useText()
  const { language } = useLanguage()
  const { open, toggle, ref } = usePopover<HTMLDivElement>()
  const menuRef = useKeepInView<HTMLDivElement>(camera)
  const wert = element.lineHeight ?? DEFAULT_LINE_HEIGHT

  return (
    <div className="text-lineheight" ref={ref}>
      <button
        type="button"
        className={`text-lineheight-trigger${open ? ' is-on' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        title={t('text.lineHeightTitle')}
      >
        <span aria-hidden="true">≡</span>
        {formatNumber(wert, language, 2)}
      </button>

      {open && (
        <div className="text-menu is-wide" ref={menuRef}>
          <span className="text-menu-label">{t('text.lineHeight')}</span>
          <div className="text-menu-row">
            {LINE_HEIGHTS.map((vorgabe) => (
              <button
                key={vorgabe}
                type="button"
                className={`text-menu-chip${Math.abs(wert - vorgabe) < 0.001 ? ' is-on' : ''}`}
                onClick={() => onChange({ lineHeight: vorgabe })}
              >
                {formatNumber(vorgabe, language, 2)}
              </button>
            ))}
          </div>
          <MeasureField
            value={wert}
            min={MIN_LINE_HEIGHT}
            max={MAX_LINE_HEIGHT}
            step={0.05}
            digits={2}
            title={t('text.lineHeight')}
            onCommit={(lineHeight) => onChange({ lineHeight })}
          />
        </div>
      )}
    </div>
  )
}

export function TextSettings({
  element,
  theme,
  fonts,
  camera,
  editor,
  onChange,
  barRef,
  editing,
  scaleFont,
  onToggleScaleFont,
}: Props): React.ReactElement {
  const t = useText()

  /** Eine Marke setzen: beim Tippen auf der Markierung, sonst am ganzen Feld. */
  const marke = (
    name: 'bold' | 'italic' | 'underline',
    elementSeitig?: () => void,
  ): (() => void) => () => {
    if (editor) {
      editor.chain().focus().toggleMark(name).run()
      return
    }
    elementSeitig?.()
  }

  const aktiv = (name: string): boolean => editor?.isActive(name) ?? false

  return (
    <div
      className="text-settings"
      ref={barRef}
      onPointerDown={(event) => {
        event.stopPropagation()
        // Sonst nimmt der Klick auf die Leiste dem Editor den Fokus und damit
        // die Markierung, auf die der Knopf gerade wirken soll. Eingabefelder
        // brauchen den Fokus allerdings selbst.
        if (editing && !(event.target instanceof HTMLInputElement)) event.preventDefault()
      }}
    >
      <FontChoice
        element={element}
        theme={theme}
        fonts={fonts}
        camera={camera}
        onChange={onChange}
      />

      <MeasureField
        value={element.fontSize}
        min={MIN_FONT_SIZE}
        max={MAX_FONT_SIZE}
        // Der Schritt wächst mit der Größe: Bei 240 sind zwei Punkte kein
        // Unterschied, den man auf der Folie sähe.
        step={element.fontSize >= 120 ? 8 : element.fontSize >= 48 ? 4 : 2}
        title={t('text.size')}
        onCommit={(fontSize) => onChange({ fontSize })}
      />

      <span className="text-settings-divider" />

      <button
        type="button"
        className={(editor ? aktiv('bold') : element.bold) ? 'is-on' : undefined}
        onClick={marke('bold', () => onChange({ bold: !element.bold }))}
        title={t('text.bold')}
      >
        <b>B</b>
      </button>
      <button
        type="button"
        className={aktiv('italic') ? 'is-on' : undefined}
        onClick={marke('italic')}
        disabled={!editor}
        title={t('text.italic')}
      >
        <em>I</em>
      </button>
      <button
        type="button"
        className={aktiv('underline') ? 'is-on' : undefined}
        onClick={marke('underline')}
        disabled={!editor}
        title={t('text.underline')}
      >
        <u>U</u>
      </button>
      <button
        type="button"
        className={aktiv('bulletList') ? 'is-on' : undefined}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        disabled={!editor}
        title={t('text.list')}
      >
        •—
      </button>

      {(['left', 'center', 'right'] as const).map((align) => (
        <button
          key={align}
          type="button"
          className={
            (editor ? editor.isActive({ textAlign: align }) : element.align === align)
              ? 'is-on'
              : undefined
          }
          onClick={() => {
            if (editor) editor.chain().focus().setTextAlign(align).run()
            else onChange({ align })
          }}
          title={t(`text.align.${align}` as const)}
        >
          {align === 'left' ? '⇤' : align === 'center' ? '↔' : '⇥'}
        </button>
      ))}

      <ColorPicker
        value={element.color}
        onChange={(color) => {
          // Beim Tippen färbt die Markierung, sonst das ganze Feld.
          if (editor) editor.chain().focus().setColor(color).run()
          else onChange({ color })
        }}
        palette={theme.palette}
        scaled
        camera={camera}
        title={t('text.color')}
      />

      <span className="text-settings-divider" />

      <LineSpacing element={element} camera={camera} onChange={onChange} />

      <button
        type="button"
        className={element.autoHeight ? undefined : 'is-on'}
        onClick={() => onChange({ autoHeight: !element.autoHeight })}
        title={element.autoHeight ? t('text.heightAutoTitle') : t('text.heightFixedTitle')}
      >
        {element.autoHeight ? '↕' : '⇕'}
      </button>

      {/* Senkrechte Ausrichtung gibt es nur, wo es Platz zu verteilen gibt. */}
      {!element.autoHeight &&
        (['top', 'middle', 'bottom'] as const).map((valign) => (
          <button
            key={valign}
            type="button"
            className={element.valign === valign ? 'is-on' : undefined}
            onClick={() => onChange({ valign })}
            title={t(`text.valign.${valign}` as const)}
          >
            {valign === 'top' ? '⤒' : valign === 'middle' ? '⤢' : '⤓'}
          </button>
        ))}

      {onToggleScaleFont && (
        <button
          type="button"
          className={scaleFont ? 'is-on' : undefined}
          onClick={onToggleScaleFont}
          aria-pressed={scaleFont}
          title={scaleFont ? t('text.scaleFontOnTitle') : t('text.scaleFontOffTitle')}
        >
          ⤡
        </button>
      )}
    </div>
  )
}
